import { getUploadLogger, UploadLogger } from './upload-logger'

export class RetryHandler {
  private readonly defaultMaxRetries = 3
  private readonly logger: UploadLogger

  constructor(logger: UploadLogger = getUploadLogger()) {
    this.logger = logger
  }

  async executeWithRetry<T>(
    fn: () => Promise<T>,
    maxRetries: number = this.defaultMaxRetries
  ): Promise<T> {
    let lastError: Error

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn()
      } catch (error) {
        lastError = error as Error

        this.logger.warn(`Retry attempt ${attempt}/${maxRetries} failed`, {
          attempt,
          maxRetries,
          errorMessage: lastError.message,
          errorName: lastError.name,
          errorCode:
            (error as { code?: string })?.code ||
            (error as { response?: { status?: number } })?.response?.status
        })

        if (attempt === maxRetries) {
          this.logger.error(
            `All ${maxRetries} attempts failed. Giving up.`,
            lastError,
            {
              attempt,
              maxRetries
            }
          )
          throw lastError
        }

        const backoffDelay = this.calculateBackoff(attempt)
        this.logger.debug(
          `Waiting ${backoffDelay}ms before retry attempt ${attempt + 1}`,
          {
            backoffMs: backoffDelay,
            nextAttempt: attempt + 1
          }
        )
        await this.sleep(backoffDelay)
      }
    }

    throw lastError!
  }

  /**
   * Backoff exponencial com jitter aleatório de até 30%.
   * O jitter evita "thundering herd" quando múltiplas requisições falham
   * simultaneamente e tentam novamente ao mesmo tempo.
   */
  calculateBackoff(attempt: number): number {
    const exponentialDelay = Math.pow(2, attempt - 1) * 1000
    const jitter = Math.random() * 0.3 * exponentialDelay
    return Math.round(exponentialDelay + jitter)
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

export const retryHandler = new RetryHandler()
